import React, { useCallback, useRef, ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { FiMail, FiUser, FiLock, FiCamera } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import api from '../../services/api';
import getValidationErrors from '../../utils/getValidationErrors';
import { useToast } from '../../hooks/Toast';

import Input from '../../components/Input';
import Button from '../../components/Button';

import { Container, Content, AvatarInput } from './styles';
import { useAuth } from '../../hooks/Auth';
import { ActiveSystemKindOptions } from '../../utils/appInfo';
import IProfileFormData from './IProfileFormData';

import Header from '../../components/Header';
const Profile: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const history = useHistory();
  const { user, updateUser } = useAuth();

  const handleSubmit = useCallback(
    async (data: IProfileFormData) => {
      formRef.current?.setErrors({});
      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome é obrigatório'),
          email: Yup.string().required('E-mail é obrigatório').email('Informe um e-mail válido'),
          old_password: Yup.string(),
          password: Yup.string().when('old_password', {
            is: val => !!val.length,
            then: Yup.string().required('Campo obrigatório'),
            otherwise: Yup.string(),
          }),
          password_confirmation: Yup.string()
            .when('old_password', {
              is: val => !!val.length,
              then: Yup.string().required('Campo obrigatório'),
              otherwise: Yup.string(),
            })
            .oneOf([Yup.ref('password')], 'Confirme sua senha'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const formData = {
          name: data.name,
          email: data.email,
          ...(data.old_password
            ? {
                old_password: data.old_password,
                password: data.password,
                password_confirmation: data.password_confirmation,
              }
            : {}),
        };
        const response = await api.put('/profile', formData);

        updateUser(response.data);

        if (
          user &&
          user.empresa &&
          user.empresa.active_system_kind === ActiveSystemKindOptions.Option_FinancialAccounts
        ) {
          history.push('/dashboard');
        } else {
          if (user.empresa.active_system_kind === ActiveSystemKindOptions.Option_CostAnalysis) {
            history.push('/dashboardpowerbireportsbi');
          } else {
            history.push('/dashboardpowerbi');
          }
        }

        // history.push('/home');

        addToast({
          type: 'success',
          title: 'Perfil atualizado!',
          description: 'Suas informações do perfil foram atualizadas com sucesso!',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização do perfil',
          description: 'Ocorreu um erro ao realizar a atualização do perfil, por favor tente novamente.',
        });
      }
    },
    [addToast, history, updateUser, user],
  );

  const handleAvatarChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const data = new FormData();

        data.append('avatar', e.target.files[0]);

        api.patch('/users/avatar', data).then(response => {
          updateUser(response.data);

          addToast({
            type: 'success',
            title: 'Avatar atualizado',
          });
        });
      }
    },
    [addToast, updateUser],
  );

  return (
    <Container>
      <Header />

      <Content>
        <Form
          ref={formRef}
          initialData={{
            name: user.name,
            email: user.email,
          }}
          onSubmit={handleSubmit}
        >
          <AvatarInput>
            {user.avatar_url === '' || user.avatar_url === null || user.avatar_url === undefined ? (
              <FiUser />
            ) : (
              <img src={user.avatar_url} alt={user.name} />
            )}

            <label htmlFor="avatar">
              <FiCamera />
              <input type="file" id="avatar" onChange={handleAvatarChange} />
            </label>
          </AvatarInput>

          <h1>Meu perfil</h1>

          <Input name="name" icon={FiUser} placeholder="Nome" />
          <Input name="email" icon={FiMail} placeholder="E-mail" />

          <Input
            containerStyle={{ marginTop: 24 }}
            name="old_password"
            icon={FiLock}
            type="password"
            placeholder="Senha atual"
          />

          <Input name="password" icon={FiLock} type="password" placeholder="Nova Senha" />

          <Input name="password_confirmation" icon={FiLock} type="password" placeholder="Confirmar senha" />
          <Button type="submit">Confirmar mudanças</Button>
        </Form>

        <span
          style={{
            fontSize: '9px',
          }}
        >
          versão 1.0.22 - 2025
        </span>
      </Content>
    </Container>
  );
};

export default Profile;
